import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { gsap, isCompactViewport, prefersReducedMotion, registerGsap } from '@/lib/gsap'
import { Float, Reveal, Spin } from '@/components/anim'

const profilePics = ['Ellipse-12', 'Ellipse-13', 'Ellipse-15', 'Ellipse-14']

export const FeaturesSection: FC = (): ReactNode => {
    const profileRef = useRef<HTMLDivElement | null>(null)

    // Webflow a-12 (container fade+move) + a-78 (avatars scale-stagger) share a
    // single scroll-into-view trigger.
    useEffect(() => {
        registerGsap()
        const el = profileRef.current
        if (!el) return
        if (prefersReducedMotion() || isCompactViewport()) return
        const holders = el.querySelectorAll('.profile-picture-holder')

        const ctx = gsap.context(() => {
            gsap
                .timeline({ scrollTrigger: { trigger: el, start: 'top 85%', once: true } })
                .fromTo(el, { opacity: 0, y: 25 }, { opacity: 1, y: 0, duration: 1, ease: 'wfEase' }, 0)
                .fromTo(
                    holders,
                    { opacity: 0, scale: 0.5 },
                    { opacity: 1, scale: 1, duration: 0.6, stagger: 0.12, ease: 'wfEase' },
                    0
                )
        }, el)

        return () => ctx.revert()
    }, [])

    return (
        <div id='features' className='section overflow-hidden zindex2'>
            <div className='container'>
                <div className='content'>
                    <div className='text-holder'>
                        <Reveal as='h2' className='hero-text'>
                            Get your <span className='gradient-text'>moneys</span> worth
                        </Reveal>
                        <div className='fixed-text'>
                            From financial metrics to sales and marketing data, our dashboard provides
                            you with a holistic view of your business operations.
                        </div>
                    </div>

                    <div className='w-layout-grid task-grid'>
                        <div id='w-node-_9632c56b-48f6-439c-a861-338feaabd880-c63dcf76' className='task-grid-item'>
                            <div className='task-item-text-holder'>
                                <h3 className='task-title'>Task Creation and Editing</h3>
                                <div className='grey-text'>
                                    Users can easily add, edit, and delete tasks, allowing for flexible
                                    task management.
                                </div>
                            </div>
                            <div className='task-creation-image-holder _01'>
                                <Spin as='div' className='cone-image-holder' duration={14}>
                                    <img
                                        src='/images/Cone-2.webp'
                                        loading='lazy'
                                        width={299}
                                        height={357}
                                        alt=''
                                        className='cone-image'
                                    />
                                </Spin>
                                <img
                                    src='/images/Task-Creation.webp'
                                    loading='lazy'
                                    width={1158}
                                    height={666}
                                    alt='Task creation interface'
                                    className='task-creation-image'
                                />
                            </div>
                        </div>

                        <div id='w-node-_1c89c938-d708-c907-0792-47c644b81930-c63dcf76' className='task-grid-item _02'>
                            <div className='task-item-text-holder'>
                                <h3 className='task-title'>Task Creation and Editing</h3>
                                <div className='grey-text'>
                                    Tasks can be categorized or labeled based on projects, tags, or themes
                                    for better organization.
                                </div>
                            </div>
                            <div className='profile-pictures' ref={profileRef}>
                                {profilePics.map((pic, index) => (
                                    <div className={`profile-picture-holder _0${index + 1}`} key={pic}>
                                        <div className='profile-image-square'>
                                            <img
                                                src={`/images/${pic}.webp`}
                                                loading='lazy'
                                                alt=''
                                                className='profile-picture'
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div className='profile-picture-holder _04'>
                                    <div className='profile-image-square'>
                                        <div className='profile-number-holder'>
                                            <div>+2</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div id='w-node-cb4b8db8-3888-023b-11f9-63537c75219c-c63dcf76' className='task-grid-item'>
                            <div className='task-item-text-holder'>
                                <h3 className='task-title'>Progress Tracking</h3>
                                <div className='grey-text'>
                                    Users can track the progress of tasks, indicating whether they are
                                    incomplete, in progress, or completed.
                                </div>
                            </div>
                            <div className='task-creation-image-holder'>
                                <img
                                    src='/images/Tracking.webp'
                                    loading='lazy'
                                    width={684}
                                    height={366}
                                    alt='Progress tracking'
                                    className='task-creation-image'
                                />
                            </div>
                        </div>

                        <div id='w-node-_3183f058-02b5-9d38-7ba3-52e1931c818d-c63dcf76' className='task-grid-item'>
                            <div className='task-item-text-holder'>
                                <h3 className='task-title'>Task Dependencies</h3>
                                <div className='grey-text'>
                                    Users can establish relationships between tasks, where one task must be
                                    completed before another can begin.
                                </div>
                            </div>
                            <div className='task-creation-image-holder _02'>
                                <img
                                    src='/images/Group-652.webp'
                                    loading='lazy'
                                    width={628}
                                    height={354}
                                    alt='Task dependencies'
                                    className='task-creation-image _02'
                                />
                            </div>
                        </div>

                        <div id='w-node-_7a3d1f04-34ae-009b-e3a0-766db3543743-c63dcf76' className='task-grid-item'>
                            <div className='task-item-text-holder'>
                                <h3 className='task-title'>Data Export/Integration</h3>
                                <div className='grey-text'>
                                    Users may have the option to export task data or integrate the dashboard
                                    with other productivity tools or calendars.
                                </div>
                            </div>
                            <div className='task-creation-image-holder'>
                                <img
                                    src='/images/Todays-Sales.webp'
                                    loading='lazy'
                                    width={1106}
                                    height={362}
                                    alt='Data export and integration'
                                    className='task-creation-image'
                                />
                            </div>
                        </div>

                        <div id='w-node-_718f07ba-cfdf-8be0-5058-109368d8cf6c-c63dcf76' className='task-grid-item image'>
                            <div className='task-item-text-holder collaboration'>
                                <h3 className='task-title'>Collaboration and Sharing</h3>
                                <div>
                                    Users can share tasks or collaborate with team members, assigning tasks,
                                    leaving comments, or sharing files.
                                </div>
                            </div>
                            <Reveal
                                as='img'
                                variant='scale'
                                fromScale={1.1}
                                duration={1.1}
                                className='collaboration-image'
                                src='/images/Collaboration-Photo.webp'
                                loading='lazy'
                                alt='Team collaboration'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
